import React, { useCallback, useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { api, apiError } from "@/lib/api";
import { toast } from "sonner";
import {
  EnvelopeIcon,
  CheckCircleIcon,
  XCircleIcon,
  ClockIcon,
  ArrowClockwiseIcon,
  PaperPlaneTiltIcon,
  WarningIcon,
  EyeIcon,
  CursorClickIcon,
  EnvelopeOpenIcon,
  ProhibitIcon,
  TrashIcon,
} from "@phosphor-icons/react";

const STATUS_STYLES = {
  delivered: { bg: "bg-emerald-50", text: "text-emerald-700", border: "border-emerald-200", Icon: CheckCircleIcon },
  opened:    { bg: "bg-sky-50",     text: "text-sky-700",     border: "border-sky-200",     Icon: EnvelopeOpenIcon },
  clicked:   { bg: "bg-violet-50",  text: "text-violet-700",  border: "border-violet-200",  Icon: CursorClickIcon },
  sent:      { bg: "bg-emerald-50/60", text: "text-emerald-600", border: "border-emerald-100", Icon: PaperPlaneTiltIcon },
  delayed:   { bg: "bg-amber-50",   text: "text-amber-700",   border: "border-amber-200",   Icon: ClockIcon },
  bounced:   { bg: "bg-red-50",     text: "text-red-700",     border: "border-red-200",     Icon: XCircleIcon },
  complained:{ bg: "bg-red-100",    text: "text-red-800",     border: "border-red-300",     Icon: ProhibitIcon },
  failed:    { bg: "bg-red-50",     text: "text-red-700",     border: "border-red-200",     Icon: XCircleIcon },
  skipped:   { bg: "bg-amber-50",   text: "text-amber-700",   border: "border-amber-200",   Icon: ClockIcon },
};

function fmtTime(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return d.toLocaleString(undefined, {
      month: "short", day: "numeric", hour: "numeric", minute: "2-digit",
    });
  } catch {
    return iso;
  }
}

export default function AdminEmailLog() {
  const [search, setSearch] = useState("");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [resendUser, setResendUser] = useState(""); // user_id input for manual re-send
  const [resending, setResending] = useState(false);

  const refresh = useCallback(async (filterEmail) => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/email-log", {
        params: filterEmail ? { email: filterEmail.trim().toLowerCase(), limit: 100 } : { limit: 100 },
      });
      setItems(data.items || []);
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setLoading(false);
    }
  }, []);

  // Detect the Resend "domain not verified" failure pattern so we can show a
  // single high-signal banner instead of expecting the admin to read every row.
  // Only flag failures from the LAST HOUR — old historical failures (before the
  // domain was verified) shouldn't keep the banner up forever.
  const ONE_HOUR_MS = 60 * 60 * 1000;
  const now = Date.now();
  const domainBlocked = items.some((it) => {
    if (it.status !== "failed" || !it.error) return false;
    if (!/verify a domain|testing emails to your own/i.test(it.error)) return false;
    const t = it.sent_at ? Date.parse(it.sent_at) : 0;
    return t && now - t < ONE_HOUR_MS;
  });

  useEffect(() => {
    refresh();
  }, [refresh]);

  const onSearch = (e) => {
    e.preventDefault();
    refresh(search);
  };

  const onClear = () => {
    setSearch("");
    refresh();
  };

  const resendWelcome = async () => {
    const userId = resendUser.trim();
    if (!userId) {
      toast.error("Enter a user_id to re-send their welcome email");
      return;
    }
    setResending(true);
    try {
      const { data } = await api.post(`/admin/resend-welcome/${userId}`);
      toast.success(`Welcome email re-sent to ${data.to}`);
      setResendUser("");
      // Briefly wait for the dispatch task, then refresh.
      setTimeout(() => refresh(search), 1500);
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setResending(false);
    }
  };

  const [clearing, setClearing] = useState(false);
  const clearLog = async (scope) => {
    const labels = {
      failed:
        "Delete every FAILED / bounced / complained / delayed email log entry?\n\n" +
        "Stale failures from before fixing your sender domain will be removed. " +
        "Successfully delivered entries are kept.",
      all:
        "Delete the ENTIRE email log?\n\n" +
        "All audit history (delivered, opened, clicked, failed) will be lost. " +
        "This cannot be undone.",
      email: search.trim()
        ? `Delete every log entry sent to "${search.trim().toLowerCase()}"?`
        : "",
    };
    const msg = labels[scope];
    if (!msg) return;
    if (!window.confirm(msg)) return;
    setClearing(true);
    try {
      const params = { scope };
      if (scope === "email") params.email = search.trim().toLowerCase();
      const { data } = await api.delete("/admin/email-log", { params });
      toast.success(
        data.deleted === 0
          ? "Nothing to delete — log already clean."
          : `Cleared ${data.deleted} ${data.deleted === 1 ? "entry" : "entries"}.`
      );
      refresh(search);
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setClearing(false);
    }
  };

  return (
    <div className="mt-10 border border-zinc-200 bg-white rounded-2xl" data-testid="admin-email-log">
      <div className="p-4 border-b border-zinc-200 flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-2">
          <EnvelopeIcon size={20} className="text-[#1D78AB]" />
          <div className="font-display font-bold">Transactional email log</div>
          <span className="text-xs text-zinc-500">
            ({items.length} {items.length === 1 ? "entry" : "entries"})
          </span>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Button
            variant="outline"
            size="sm"
            onClick={() => clearLog("failed")}
            disabled={clearing || loading}
            className="text-amber-700 border-amber-300 hover:bg-amber-50"
            data-testid="email-log-clear-failed"
            title="Delete only failed/bounced/complained/delayed entries"
          >
            <TrashIcon size={14} className="mr-1.5" />
            Clear failed
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => clearLog("all")}
            disabled={clearing || loading}
            className="text-red-600 border-red-300 hover:bg-red-50"
            data-testid="email-log-clear-all"
            title="Delete every entry — full audit history is lost"
          >
            <TrashIcon size={14} className="mr-1.5" />
            {clearing ? "Clearing…" : "Clear all"}
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => refresh(search)}
            disabled={loading}
            data-testid="email-log-refresh"
          >
            <ArrowClockwiseIcon size={14} className="mr-1.5" />
            {loading ? "Loading…" : "Refresh"}
          </Button>
        </div>
      </div>

      {/* Resend domain-not-verified warning */}
      {domainBlocked && (
        <div
          className="mx-4 mt-4 rounded-xl bg-amber-50 border border-amber-300 p-4 flex items-start gap-3"
          data-testid="email-log-domain-warning"
        >
          <WarningIcon size={20} weight="fill" className="text-amber-600 shrink-0 mt-0.5" />
          <div className="text-sm text-amber-900 leading-relaxed">
            <div className="font-bold mb-1">Resend is rejecting emails to most recipients.</div>
            <p>
              Right now Resend only allows sending to your account email
              (<span className="font-mono">sunsetsdealz@gmail.com</span>). To send
              welcome &amp; verification emails to <b>real customers</b>, you must
              verify the <span className="font-mono">bidorah.com</span> domain in
              your Resend dashboard.
            </p>
            <a
              href="https://resend.com/domains"
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-1 mt-2 font-bold text-amber-900 underline underline-offset-2 hover:text-amber-700"
              data-testid="resend-domains-link"
            >
              Open resend.com/domains →
            </a>
            <p className="mt-2 text-xs text-amber-800">
              Add <span className="font-mono">bidorah.com</span> · Resend will give
              you 3-4 DNS records (TXT &amp; CNAME) to add at your domain registrar.
              Once verified, every welcome / verified email starts delivering
              automatically — no code changes needed.
            </p>
          </div>
        </div>
      )}

      {/* Search + manual re-send */}
      <div className="p-4 border-b border-zinc-100 grid md:grid-cols-2 gap-3">
        <form onSubmit={onSearch} className="flex gap-2" data-testid="email-log-search-form">
          <Input
            type="email"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Filter by recipient email…"
            className="flex-1"
            data-testid="email-log-search-input"
          />
          <Button type="submit" data-testid="email-log-search-submit">Search</Button>
          {search && (
            <>
              <Button type="button" variant="outline" onClick={onClear} data-testid="email-log-clear">
                Clear
              </Button>
              <Button
                type="button"
                variant="outline"
                className="text-red-600 border-red-300 hover:bg-red-50"
                onClick={() => clearLog("email")}
                disabled={clearing || loading}
                title={`Delete log entries for ${search.trim().toLowerCase()}`}
                data-testid="email-log-delete-matches"
              >
                <TrashIcon size={14} />
              </Button>
            </>
          )}
        </form>
        <div className="flex gap-2" data-testid="email-log-resend-form">
          <Input
            value={resendUser}
            onChange={(e) => setResendUser(e.target.value)}
            placeholder="user_id to re-send welcome…"
            className="flex-1 font-mono text-xs"
            data-testid="email-log-resend-input"
          />
          <Button
            onClick={resendWelcome}
            disabled={resending || !resendUser.trim()}
            className="bg-[#1D78AB] hover:bg-[#165d85] text-white"
            data-testid="email-log-resend-submit"
          >
            <PaperPlaneTiltIcon size={14} className="mr-1.5" />
            {resending ? "Sending…" : "Re-send welcome"}
          </Button>
        </div>
      </div>

      {/* List */}
      {items.length === 0 ? (
        <div className="p-12 text-center text-zinc-500" data-testid="email-log-empty">
          <EnvelopeIcon size={32} className="mx-auto mb-2 opacity-40" />
          <p>{loading ? "Loading…" : "No emails logged yet."}</p>
        </div>
      ) : (
        <div className="divide-y divide-zinc-100">
          {items.map((it, idx) => {
            const style = STATUS_STYLES[it.status] || STATUS_STYLES.skipped;
            const { Icon } = style;
            return (
              <div
                key={`${it.resend_id || "no-id"}-${idx}`}
                className="p-4 flex items-start gap-4"
                data-testid={`email-log-row-${idx}`}
              >
                <div
                  className={`h-9 w-9 rounded-full ${style.bg} ${style.border} border grid place-items-center shrink-0`}
                >
                  <Icon size={18} className={style.text} weight="fill" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-semibold text-sm text-zinc-900 truncate">
                      {it.subject || "(no subject)"}
                    </span>
                    <span
                      className={`text-[10px] uppercase tracking-wider font-bold px-2 py-0.5 rounded-full ${style.bg} ${style.text}`}
                    >
                      {it.status}
                    </span>
                    {it.last_event && it.last_event !== `email.${it.status}` && (
                      <span className="text-[10px] uppercase tracking-wider text-zinc-500" title={it.last_event_at}>
                        · {it.last_event.replace("email.", "")}
                      </span>
                    )}
                  </div>
                  <div className="text-xs text-zinc-600 mt-1 truncate">
                    To <span className="font-mono">{it.to}</span> · {fmtTime(it.sent_at)}
                    {it.last_event_at && it.last_event_at !== it.sent_at && (
                      <span className="text-zinc-400"> → {fmtTime(it.last_event_at)}</span>
                    )}
                  </div>
                  {it.resend_id && (
                    <div className="text-[11px] text-zinc-400 font-mono mt-0.5 truncate">
                      resend_id: {it.resend_id}
                    </div>
                  )}
                  {it.error && (
                    <div className="text-xs text-red-600 mt-1.5 bg-red-50 border border-red-100 rounded px-2 py-1">
                      {it.error}
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
